import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingBag, Check } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/constants";
import { useBasket } from "@/context/BasketContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import SmartImage from "@/components/SmartImage";

export default function ProductDetailPage() {
  const { id } = useParams();
  const { add, has } = useBasket();
  const [product, setProduct] = useState(null);
  const [missing, setMissing] = useState(false);
  const [showBack, setShowBack] = useState(false);

  useEffect(() => {
    setProduct(null);
    setMissing(false);
    setShowBack(false);
    window.scrollTo(0, 0);
    axios
      .get(`${API}/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setMissing(true));
  }, [id]);

  if (missing) {
    return (
      <main className="bg-white min-h-screen font-body">
        <Navbar />
        <div className="pt-40 pb-32 text-center px-4" data-testid="product-not-found">
          <h1 className="font-display font-bold text-3xl text-kera-ink">Product not found</h1>
          <Link to="/products" className="mt-6 inline-block rounded-full bg-black text-white px-6 py-3 text-sm hover:bg-kera-gold hover:text-black transition-colors">
            Back to the range
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  if (!product) {
    return (
      <main className="bg-white min-h-screen font-body">
        <Navbar />
        <div className="pt-40 text-center font-mono text-xs uppercase tracking-[0.25em] text-kera-copper">Loading</div>
      </main>
    );
  }

  const inBasket = has(product.id);
  const steps = (product.cooking_instructions || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const ingredients = (product.ingredients || "").split(",").map((s) => s.trim()).filter(Boolean);
  const meta = [
    ["Product Code", product.product_code],
    ["Barcode", product.barcode],
    ["Net Weight", product.net_weight],
    ["Origin", product.country_of_origin],
  ].filter(([, v]) => v);
  const mainImage = showBack && product.back_image ? product.back_image : product.image || product.front_image;

  return (
    <main className="bg-white text-kera-ink font-body min-h-screen" data-testid="product-detail-page">
      {product && <Seo title={product.name} description={product.description} path={`/products/${product.id}`} />}
      <Navbar />
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pt-28 pb-20">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted" data-testid="product-breadcrumb">
          <Link to="/products" className="hover:text-kera-copper transition-colors inline-flex items-center gap-1">
            <ArrowLeft size={12} /> Products
          </Link>
          <span className="text-kera-line">/</span>
          <span className="text-kera-copper">{product.category}</span>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-kera-gold/40 rounded-2xl translate-x-3 translate-y-3" />
            <SmartImage
              src={mainImage}
              alt={`KERA ${product.name}`}
              eager
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="relative rounded-2xl w-full aspect-square object-cover border border-kera-line"
              data-testid="product-detail-image"
            />
            {product.back_image && (
              <div className="relative mt-4 flex gap-2" data-testid="product-image-toggle">
                {[["front", product.image || product.front_image, "Front"], ["back", product.back_image, "Back"]].map(([key, src, label]) => (
                  src ? (
                    <button
                      key={key}
                      onClick={() => setShowBack(key === "back")}
                      data-testid={`product-image-${key}`}
                      className={`rounded-lg overflow-hidden border-2 transition-colors ${((key === "back") === showBack) ? "border-kera-gold" : "border-kera-line"}`}
                    >
                      <SmartImage src={src} alt={label} sizes="64px" className="h-16 w-16 object-cover" />
                    </button>
                  ) : null
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-kera-copper">{product.category}</p>
              {product.new_product && (
                <span className="rounded-full bg-kera-gold text-black px-3 py-0.5 text-[10px] font-mono uppercase tracking-[0.15em]" data-testid="product-new-badge">New</span>
              )}
            </div>
            <h1 className="mt-2 font-display font-bold text-3xl sm:text-4xl text-kera-ink" data-testid="product-detail-name">
              KERA {product.name}
            </h1>
            {product.description && (
              <p className="mt-4 text-base sm:text-lg text-kera-muted leading-relaxed">{product.description}</p>
            )}
            {product.long_description && (
              <p className="mt-3 text-sm sm:text-base text-kera-muted/90 leading-relaxed">{product.long_description}</p>
            )}

            {meta.length > 0 && (
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="product-meta">
                {meta.map(([label, value]) => (
                  <div key={label} className="rounded-xl border border-kera-line px-3.5 py-2.5">
                    <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-kera-muted">{label}</p>
                    <p className="mt-1 text-sm font-medium text-kera-ink">{value}</p>
                  </div>
                ))}
              </div>
            )}

            {product.pack_sizes?.length > 0 && (
              <div className="mt-6" data-testid="product-pack-sizes">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-copper mb-2.5">Pack Sizes</p>
                <div className="flex flex-wrap gap-2">
                  {product.pack_sizes.map((size) => (
                    <span key={size} className="rounded-full border border-kera-gold/60 bg-white px-4 py-1.5 text-xs text-kera-ink">
                      {size}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => {
                if (!inBasket) {
                  add(product);
                  toast.success(`${product.name} added to your enquiry list`);
                }
              }}
              data-testid="product-add-to-basket"
              className={`mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold tracking-wide transition-colors duration-300 ${
                inBasket
                  ? "bg-kera-gold/20 text-kera-copper border border-kera-gold/60 cursor-default"
                  : "bg-kera-gold text-black hover:bg-black hover:text-kera-gold"
              }`}
            >
              {inBasket ? <Check size={16} /> : <ShoppingBag size={16} />}
              {inBasket ? "In your enquiry list" : "Add to Enquiry List"}
            </button>
            {inBasket && (
              <Link to="/basket" className="ml-4 text-xs font-mono uppercase tracking-[0.18em] text-kera-copper border-b border-kera-gold pb-0.5 hover:text-kera-ink transition-colors" data-testid="product-go-to-basket">
                Review &amp; send
              </Link>
            )}
          </motion.div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {ingredients.length > 0 && (
            <div className="rounded-2xl border border-kera-line bg-white p-6" data-testid="product-ingredients">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-copper mb-4">Ingredients</p>
              <div className="flex flex-wrap gap-2">
                {ingredients.map((ing) => (
                  <span key={ing} className="rounded-full bg-[#FAF7F0] border border-kera-line px-3.5 py-1.5 text-xs text-kera-ink">
                    {ing}
                  </span>
                ))}
              </div>
            </div>
          )}
          {steps.length > 0 && (
            <div className="rounded-2xl border border-kera-line bg-white p-6" data-testid="product-cooking">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-copper mb-4">Cooking Instructions</p>
              <ol className="space-y-3">
                {steps.map((step, i) => (
                  <li key={i} className="flex gap-3 text-sm text-kera-muted leading-relaxed">
                    <span className="shrink-0 h-6 w-6 rounded-full bg-black text-kera-gold text-[11px] font-mono flex items-center justify-center">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}
          {product.nutrition?.length > 0 && (
            <div className="rounded-2xl border border-kera-line bg-white p-6" data-testid="product-nutrition">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-copper mb-4">Nutrition · per 100g</p>
              <dl className="divide-y divide-kera-line/70">
                {product.nutrition.map((n) => (
                  <div key={n.label} className="flex justify-between py-2 text-sm">
                    <dt className="text-kera-muted">{n.label}</dt>
                    <dd className="text-kera-ink font-medium">{n.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {product.allergen_information && (
            <div className="rounded-2xl border border-kera-gold/50 bg-[#FAF7F0] p-6" data-testid="product-allergens">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-copper mb-4">Allergen Information</p>
              <p className="text-sm text-kera-ink leading-relaxed">{product.allergen_information}</p>
            </div>
          )}
          {product.storage_information && (
            <div className="rounded-2xl border border-kera-line bg-white p-6" data-testid="product-storage">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-copper mb-4">Storage</p>
              <p className="text-sm text-kera-muted leading-relaxed">{product.storage_information}</p>
            </div>
          )}
        </div>

        {product.related?.length > 0 && (
          <div className="mt-20" data-testid="product-related">
            <h2 className="font-display font-semibold text-2xl text-kera-ink mb-8">
              More from <span className="text-kera-copper">{product.category}</span>
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
              {product.related.map((p) => (
                <Link key={p.id} to={`/products/${p.id}`} className="group" data-testid={`related-${p.id}`}>
                  <div className="rounded-2xl overflow-hidden border border-kera-line">
                    <SmartImage src={p.image} alt={`KERA ${p.name}`} sizes="(max-width: 640px) 50vw, 25vw" className="w-full aspect-square object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  </div>
                  <h3 className="mt-3 font-display font-semibold text-base text-kera-ink group-hover:text-kera-copper transition-colors">
                    KERA {p.name}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
